import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import './InnovationPage.css';
import './InnovationGraph.css';

const philosophy=[
  ['01','Focus On What We Can Control','We invest our energy in actions,execution,learning,and continuous improvement rather than short-term outcomes.','FiTarget'],
  ['02','Build Strong Foundations','Sustainable growth begins with strong systems,operational discipline,and long-term thinking.','FiLayers'],
  ['03','Solve Real Problems','We pursue opportunities that create meaningful value and address genuine challenges.','FiCompass'],
  ['04','Think Long-Term','We prioritize creating lasting impact over chasing immediate results.','FiTrendingUp']
];

const innovationModes=[
  ['Process Innovation','Finding better ways to improve efficiency,quality,and execution.','FiRefreshCw','01'],
  ['Business Innovation','Creating new solutions,services,partnerships,and opportunities.','FiBriefcase','02'],
  ['Technology Innovation','Leveraging technology to strengthen operations and improve experiences.','FiCpu','03'],
  ['Strategic Innovation','Exploring opportunities that support future growth and long-term value creation.','FiCompass','04'],
  ['Operational Innovation','Building systems that increase scalability and sustainability.','FiSettings','05']
];

const learningCycle=[
  ['Observe','Understand the challenge before deciding how to respond.'],
  ['Question','Challenge assumptions and look for a clearer perspective.'],
  ['Improve','Make practical changes that create measurable progress.'],
  ['Compound','Carry the learning forward into stronger systems.']
];

function InnovationConnections() {
  return (
    <svg
      className="innovation-graph-connections"
      viewBox="0 0 520 520"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="innovation-line-gold" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stopColor="#d4af37" stopOpacity=".9" />
          <stop offset="1" stopColor="#6eaeec" stopOpacity=".45" />
        </linearGradient>
        <linearGradient id="innovation-line-blue" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#d4af37" stopOpacity=".82" />
          <stop offset="1" stopColor="#6eaeec" stopOpacity=".52" />
        </linearGradient>
      </defs>
      <line x1="260" y1="260" x2="260" y2="62" stroke="url(#innovation-line-gold)" />
      <line x1="260" y1="260" x2="468" y2="260" stroke="url(#innovation-line-blue)" />
      <line x1="260" y1="260" x2="260" y2="458" stroke="url(#innovation-line-gold)" />
      <line x1="260" y1="260" x2="52" y2="260" stroke="url(#innovation-line-blue)" />
      <circle cx="260" cy="260" r="4" />
    </svg>
  );
}

function InnovationHeroVisual() {
  return (
    <div className="innovation-hero-visual" aria-label="Connected innovation system visualization">
      <div className="innovation-visual-grid" />
      <div className="innovation-visual-orbit innovation-orbit-one" />
      <div className="innovation-visual-orbit innovation-orbit-two" />
      <InnovationConnections />
      <motion.div
        className="innovation-visual-core"
        animate={{
          boxShadow:[
            '0 0 0 0 rgba(212,175,55,.2),0 20px 60px rgba(0,0,0,.24)',
            '0 0 0 25px rgba(212,175,55,0),0 20px 60px rgba(0,0,0,.24)',
            '0 0 0 0 rgba(212,175,55,0),0 20px 60px rgba(0,0,0,.24)'
          ]
        }}
        transition={{duration:3.5,repeat:Infinity}}
      >
        <span>THE PROFITYO</span>
        <strong>WAY OF THINKING</strong>
        <small>VISION · SYSTEMS · EXECUTION</small>
      </motion.div>
      <span className="innovation-visual-label innovation-label-one">VISION</span>
      <span className="innovation-visual-label innovation-label-two">LEARNING</span>
      <span className="innovation-visual-label innovation-label-three">SYSTEMS</span>
      <span className="innovation-visual-label innovation-label-four">IMPACT</span>
    </div>
  );
}

function PhilosophyCard({item,index}) {
  return (
    <motion.article
      className="innovation-philosophy-card"
      initial={{opacity:0,y:20}}
      whileInView={{opacity:1,y:0}}
      viewport={{once:true,amount:.15}}
      transition={{delay:index*.08}}
      whileHover={{y:-7}}
    >
      <div className="innovation-card-top">
        <span>{item[0]}</span>
        <SafeIcon name={item[3].replace('Fi','')} />
      </div>
      <h3>{item[1]}</h3>
      <p>{item[2]}</p>
      <SafeIcon icon={FiIcons.FiArrowUpRight} className="innovation-card-arrow" />
    </motion.article>
  );
}

function SystemsVisual() {
  const nodes=[
    ['People','FiUsers','systems-node-people'],
    ['Systems','FiLayers','systems-node-systems'],
    ['Technology','FiCpu','systems-node-technology'],
    ['Partnerships','FiShare2','systems-node-partnerships'],
    ['Execution','FiActivity','systems-node-execution'],
    ['Results','FiTrendingUp','systems-node-results']
  ];

  return (
    <div className="systems-visual">
      <div className="systems-visual-ring systems-ring-large" />
      <div className="systems-visual-ring systems-ring-small" />
      <div className="systems-visual-line systems-line-one" />
      <div className="systems-visual-line systems-line-two" />
      <div className="systems-visual-line systems-line-three" />
      <div className="systems-visual-line systems-line-four" />
      <div className="systems-visual-line systems-line-five" />
      <div className="systems-visual-line systems-line-six" />
      <div className="systems-core">
        <span>THE SYSTEM</span>
        <strong>CREATES<br />SCALE</strong>
      </div>
      {nodes.map(([name,icon,className])=>(
        <div className={`systems-node ${className}`} key={name}>
          <SafeIcon name={icon.replace('Fi','')} />
          <span>{name}</span>
        </div>
      ))}
    </div>
  );
}

function InnovationModeCard({item,index}) {
  return (
    <motion.article
      className="innovation-mode-card"
      initial={{opacity:0,y:18}}
      whileInView={{opacity:1,y:0}}
      viewport={{once:true,amount:.12}}
      transition={{delay:index*.07}}
      whileHover={{y:-6}}
    >
      <div className="innovation-mode-icon">
        <SafeIcon name={item[2].replace('Fi','')} />
      </div>
      <span className="innovation-mode-number">{item[3]}</span>
      <h3>{item[0]}</h3>
      <p>{item[1]}</p>
      <SafeIcon icon={FiIcons.FiArrowUpRight} className="innovation-mode-arrow" />
    </motion.article>
  );
}

function LearningStep({item,index}) {
  return (
    <motion.div
      className="innovation-learning-step"
      initial={{opacity:0,x:15}}
      whileInView={{opacity:1,x:0}}
      viewport={{once:true}}
      transition={{delay:index*.08}}
    >
      <span>0{index+1}</span>
      <div>
        <h3>{item[0]}</h3>
        <p>{item[1]}</p>
      </div>
    </motion.div>
  );
}

export default function InnovationPage() {
  return (
    <div className="innovation-page">
      <section className="innovation-hero">
        <div className="innovation-hero-grid" />
        <div className="innovation-hero-glow" />
        <div className="container innovation-hero-layout">
          <motion.div
            className="innovation-hero-copy"
            initial={{opacity:0,x:-25}}
            animate={{opacity:1,x:0}}
          >
            <span className="innovation-eyebrow">Our approach to innovation</span>
            <h1>
              Innovation through <br />
              <em>vision,systems,</em> <br />
              and execution.
            </h1>
            <p>
              At ProfitYo Global System,innovation is not limited to technology.
              It is the continuous pursuit of better ways to create value,solve
              problems,build businesses,strengthen systems,and develop
              opportunities for long-term success.
            </p>
            <p className="innovation-hero-support">
              We believe innovation happens when vision,discipline,
              entrepreneurship,and continuous improvement work together.
            </p>
          </motion.div>
          <motion.div
            initial={{opacity:0,scale:.88}}
            animate={{opacity:1,scale:1}}
            transition={{duration:.7}}
          >
            <InnovationHeroVisual />
          </motion.div>
        </div>
        <div className="innovation-scroll">
          <span />
          Explore how we think
        </div>
      </section>

      <section className="innovation-philosophy-section">
        <div className="container">
          <div className="innovation-section-heading innovation-philosophy-heading">
            <div>
              <span className="innovation-eyebrow">Our philosophy</span>
              <h2>The way<br /><em>we think.</em></h2>
            </div>
            <p>
              The strongest organizations are built on strong principles. Our
              philosophy guides every decision,every venture,and every
              opportunity we pursue.
            </p>
          </div>
          <div className="innovation-philosophy-grid">
            {philosophy.map((item,index)=>(
              <PhilosophyCard key={item[0]} item={item} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="innovation-systems-section">
        <div className="container innovation-systems-layout">
          <div className="innovation-systems-copy">
            <span className="innovation-eyebrow">The systems approach</span>
            <h2>Systems<br /><em>create scale.</em></h2>
            <p>
              Success is rarely the result of isolated effort. Long-term growth
              comes from creating systems that consistently produce positive outcomes.
            </p>
            <p>
              We focus on developing repeatable processes,scalable operations,
              and sustainable frameworks that support growth across our ecosystem.
            </p>
          </div>
          <SystemsVisual />
        </div>
      </section>

      <section className="innovation-modes-section">
        <div className="container">
          <div className="innovation-section-heading centered">
            <span className="innovation-eyebrow">How we innovate</span>
            <h2>Innovation beyond<br /><em>technology.</em></h2>
            <p>
              Innovation can take many forms. We look for better ways to
              strengthen the complete system: from ideas and operations to
              partnerships and execution.
            </p>
          </div>
          <div className="innovation-mode-grid">
            {innovationModes.map((item,index)=>(
              <InnovationModeCard key={item[0]} item={item} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="innovation-learning-section">
        <div className="container innovation-learning-layout">
          <div>
            <span className="innovation-eyebrow">Continuous improvement</span>
            <h2>Always learning.<br /><em>Always improving.</em></h2>
            <p>
              Innovation is not a destination. It is a continuous process of
              learning,refining,adapting,and growing.
            </p>
            <p>
              Every challenge presents an opportunity to improve systems,
              enhance capabilities,and strengthen our foundations.
            </p>
          </div>
          <div className="innovation-learning-cycle">
            <div className="learning-cycle-line" />
            {learningCycle.map((item,index)=>(
              <LearningStep key={item[0]} item={item} index={index} />
            ))}
          </div>
        </div>
      </section>

      <section className="innovation-leadership-section">
        <div className="container innovation-leadership-layout">
          <div>
            <span className="innovation-eyebrow">A leadership mindset</span>
            <h2>Build with curiosity.<br /><em>Lead with discipline.</em></h2>
          </div>
          <div>
            <p>
              The future belongs to organizations willing to think clearly,
              learn continuously,and act with conviction. We encourage thoughtful
              experimentation while staying grounded in strong principles and
              accountable execution.
            </p>
            <p>
              That balance gives innovation its purpose: not change for its own
              sake,but progress that creates real and lasting value.
            </p>
          </div>
        </div>
      </section>

      <section className="innovation-closing-section">
        <div className="container">
          <span className="innovation-eyebrow">The ProfitYo perspective</span>
          <h2>Think beyond today.<br /><em>Build what lasts.</em></h2>
          <p>
            Innovation is how we turn vision into stronger foundations,
            meaningful opportunities,and a future built for sustainable growth.
          </p>
        </div>
      </section>
    </div>
  );
}